import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import Modal from '../../Modal/Modal';
import SellFromHomeTransactionDetails from '../../Modals/SellWizard/SellFromHomeTransactionDetails';
import SellFromHomeTransactionHistory from '../../Modals/SellWizard/SellFromHomeTransactionHistory';
import SfhModalTitle from '../../Modals/SellWizard/SfhModalTitle';
import { emptyPortfolioTransactionSingle } from '../../../store/actions/portfolio/portfolioActions';
import { useQuery } from '../../../util/hooks/useQuery';
import { PORTFOLIO_PAGE } from '../../../constants/pages';
import { pxToRem } from '../../../assets/styles/helper';

// Opens the SFH "Transaction Details" wizard when the user arrives via the
// "Verification Passed" email deep link (`/portfolio?transactionuid=<uid>`).
// The wizard self-fetches the transaction from the Uid + the current account,
// so we only need to hand it the Uid. Auth is handled upstream: PrivateRoute
// persists the full path (incl. query) and the post-login redirect restores it.
const SfhTransactionDeepLink = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const query = useQuery();

  // The backend deploys the param as lowercase `transactionuid`; accept the
  // camelCase spelling too in case the email template ever changes.
  const deepLinkUid = query.transactionuid || query.transactionUid;

  const [sfhTransactionUid, setSfhTransactionUid] = useState(null);
  const [isSfhHistoryView, setIsSfhHistoryView] = useState(false);

  useEffect(() => {
    if (!deepLinkUid) return;
    setSfhTransactionUid(deepLinkUid);
    // Strip the param once captured so a refresh or back-nav doesn't re-open it.
    history.replace(PORTFOLIO_PAGE);
  }, [deepLinkUid, history]);

  const handleClose = () => {
    dispatch(emptyPortfolioTransactionSingle());
    setSfhTransactionUid(null);
    setIsSfhHistoryView(false);
  };

  const handleViewHistory = () => setIsSfhHistoryView(true);
  const handleHistoryBack = () => setIsSfhHistoryView(false);

  return (
    <Modal
      isOpen={Boolean(sfhTransactionUid)}
      close={handleClose}
      goBack={isSfhHistoryView ? handleHistoryBack : undefined}
      title={
        <SfhModalTitle>
          {t(
            isSfhHistoryView
              ? 'sellWizard.sfhWizard.transactionHistory.title'
              : 'sellWizard.sfhWizard.transactionDetails.title',
          )}
        </SfhModalTitle>
      }
      headerBordered
      size="sm"
      maxWidth={pxToRem(640)}
      minHeight={pxToRem(640)}
      height={pxToRem(640)}
      clipContent
      closeIconVisible
      skipListeningToLocations
      dataCy="container-sfh-transaction-details"
    >
      {sfhTransactionUid &&
        (isSfhHistoryView ? (
          <SellFromHomeTransactionHistory
            lockedSnapshot={{ transactionUid: sfhTransactionUid }}
            handleClose={handleClose}
          />
        ) : (
          <SellFromHomeTransactionDetails
            lockedSnapshot={{ transactionUid: sfhTransactionUid }}
            handleClose={handleClose}
            handleViewHistory={handleViewHistory}
          />
        ))}
    </Modal>
  );
};

export default SfhTransactionDeepLink;
